import { intl } from '@fullstory/intl';
import { SetHeatmapRequest, UnnamedHeatmap } from '@fullstory/proto/fs/services/dx-objects';
import { useNotifications } from '@fullstory/react-hooks';
import type { AppDispatch } from '../../../types';
import { HeatmapsRequestActions } from '../actions';
import { hashObject } from '@fullstory/util/hash';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fullstorySelectors } from '../../../selectors';

const { getOrgId } = fullstorySelectors;

/**
 * Returns callbacks to perform mutations on saved heatmaps
 */
export const useHeatmapsActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);

  return useMemo(
    () => ({
      // We want this to happen transparently to the user, so no notifications are triggered.
      markViewed: (heatmapId: string) =>
        dispatch(
          HeatmapsRequestActions.markViewed({
            orgId,
            heatmapId,
          }),
        ),
      create: async ({ request }: { request: Omit<SetHeatmapRequest, 'orgId'> }) => {
        try {
          if (!request.heatmap) throw new Error('Invalid Heatmap');
          const response = await dispatch(
            HeatmapsRequestActions.setHeatmap({
              ...request,
              orgId,
              heatmap: {
                ...request.heatmap,
                id: '',
              },
            }),
          );
          if (!response.heatmap?.heatmap) throw new Error('Invalid Heatmap');
          notifications?.success(
            intl.formatMessage({ defaultMessage: 'Successfully created Heatmap', id: 'JmU8dq' }),
          );

          return response;
        } catch (e) {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to create Heatmap', id: '0yLslA' }),
          );
          return null;
        }
      },
      duplicate: async ({ request }: { request: Omit<SetHeatmapRequest, 'orgId'> }) => {
        try {
          if (!request.heatmap) throw new Error('Invalid Heatmap');
          const response = await dispatch(
            HeatmapsRequestActions.setHeatmap({
              ...request,
              orgId,
              heatmap: {
                ...request.heatmap,
                id: '',
                name: intl.formatMessage(
                  { defaultMessage: '{heatmapName} (copy)', id: '1F/z5Y' },
                  { heatmapName: request.heatmap.name },
                ),
              },
            }),
          );
          if (!response.heatmap?.heatmap) throw new Error('Invalid Heatmap');
          notifications?.success(
            intl.formatMessage({ defaultMessage: 'Successfully duplicated Heatmap', id: 'K+52QH' }),
          );

          return response;
        } catch (e) {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to duplicate Heatmap', id: 'LkSWLI' }),
          );
          return null;
        }
      },
      update: async ({ request }: { request: Omit<SetHeatmapRequest, 'orgId'> }) => {
        try {
          const response = await dispatch(
            HeatmapsRequestActions.setHeatmap({
              ...request,
              orgId,
            }),
          );
          if (!response.heatmap?.heatmap?.id) throw new Error('Invalid Heatmap');
          notifications?.success(
            intl.formatMessage({ defaultMessage: 'Successfully updated Heatmap', id: 'naw5Rs' }),
          );
          return response;
        } catch (e) {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to update Heatmap', id: '3W/vkB' }),
          );
          return null;
        }
      },
      star: (heatmapId: string) =>
        dispatch(HeatmapsRequestActions.starHeatmap({ orgId, heatmapId, starred: true })).catch(
          () =>
            notifications?.error(
              intl.formatMessage({ defaultMessage: 'Failed to star Heatmap', id: 'W06Tkt' }),
            ),
        ),
      unstar: (heatmapId: string) =>
        dispatch(HeatmapsRequestActions.starHeatmap({ orgId, heatmapId, starred: false })).catch(
          () =>
            notifications?.error(
              intl.formatMessage({ defaultMessage: 'Failed to unstar Heatmap', id: '9Cwru6' }),
            ),
        ),
      archive: async (heatmapId: string) => {
        try {
          const response = await dispatch(
            HeatmapsRequestActions.archiveHeatmap({
              orgId,
              heatmapId,
              archived: true,
            }),
          );
          notifications?.success(
            intl.formatMessage({ defaultMessage: 'Successfully archived Heatmap', id: 'Sa+OLL' }),
          );
          return response;
        } catch (e) {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to archive Heatmap', id: 'LtKK4o' }),
          );
          return null;
        }
      },
      restore: (heatmapId: string) =>
        dispatch(HeatmapsRequestActions.archiveHeatmap({ orgId, heatmapId, archived: false }))
          .then(() =>
            notifications?.success(
              intl.formatMessage({ defaultMessage: 'Successfully restored Heatmap', id: 'X7uuUI' }),
            ),
          )
          .catch(() =>
            notifications?.error(
              intl.formatMessage({ defaultMessage: 'Failed to restore Heatmap', id: 'jt0WFf' }),
            ),
          ),
      createUnnamedHeatmap: (heatmap: UnnamedHeatmap) => {
        const hash = `${hashObject(heatmap)}`;
        dispatch(HeatmapsRequestActions.createUnnamedHeatmap({ hash, heatmap })).catch(() =>
          console.error('Failed to save temporary Heatmap state'),
        );
        return hash;
      },
    }),
    [dispatch, orgId, notifications],
  );
};
